import { useState, useEffect } from "react";
import { Utensils, Home, Trash2 as TrashIcon, MoonStar, MessageCircle, RefreshCw, HeartHandshake, Check } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { localDate } from "@/lib/dates";

const RHYTHM_ICONS = { utensils: Utensils, home: Home, trash: TrashIcon, moon: MoonStar };

export const UsTab = ({ token, starterRequested, onMilestone, palette }) => {
  const [rhythms, setRhythms] = useState([]);
  const [starter, setStarter] = useState(null);
  const [loadingStarter, setLoadingStarter] = useState(false);
  const [homeStatus, setHomeStatus] = useState(null);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/rhythm/household`, authHeaders).then((r) => setRhythms(r.data.rhythms || [])).catch((e) => console.error(e));
    axios.get(`${API}/rhythm/home-status?date=${localDate()}`, authHeaders).then((r) => setHomeStatus(r.data)).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Cross-tab trigger: dinner prep on My Day requested a starter
  useEffect(() => {
    if (starterRequested > 0) {
      fetchStarter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire when trigger count changes
  }, [starterRequested]);

  const fetchStarter = async () => {
    setLoadingStarter(true);
    try {
      const res = await axios.get(`${API}/rhythm/starter`, authHeaders);
      setStarter(res.data.starter);
    } catch (e) {
      toast.error("Couldn't fetch a starter");
    } finally {
      setLoadingStarter(false);
    }
  };

  const homeCheckin = async () => {
    try {
      const res = await axios.post(`${API}/rhythm/home-checkin?date=${localDate()}`, {}, authHeaders);
      setHomeStatus({ checked_in_today: true, total_checkins: res.data.total_checkins });
      toast.success(res.data.garden?.points_earned ? `Home rhythm checked in — garden grew +${res.data.garden.points_earned} 🏡🌱` : "Home rhythm checked in 🏡");
      if (res.data.milestone) onMilestone(res.data.milestone);
    } catch (e) {
      toast.error("Couldn't check in");
    }
  };

  return (
    <div className="space-y-5" data-testid="rhythm-us-tab">
      {/* Conversation starter */}
      <div className="rounded-2xl p-5 text-white" style={{ backgroundColor: palette.primary }} data-testid="conversation-starter-card">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-widest opacity-90">Dinner Conversation Starter</span>
        </div>
        {starter ? (
          <p className="text-lg font-medium leading-snug mb-4" data-testid="starter-text">"{starter}"</p>
        ) : (
          <p className="text-sm opacity-90 mb-4">Draw a question to spark real talk around the table tonight.</p>
        )}
        <button
          onClick={fetchStarter}
          disabled={loadingStarter}
          className="text-sm font-bold px-4 py-2 rounded-full bg-white flex items-center gap-2"
          style={{ color: palette.primary, opacity: loadingStarter ? 0.7 : 1 }}
          data-testid="starter-generate-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loadingStarter ? "animate-spin" : ""}`} />
          {starter ? "Draw Another" : "Draw a Question"}
        </button>
      </div>

      {/* Household rhythm cards */}
      <div>
        <h2 className="text-lg font-bold font-body mb-3" style={{ color: palette.primary }}>Household Rhythm</h2>
        <div className="space-y-2.5">
          {rhythms.map((r) => {
            const Icon = RHYTHM_ICONS[r.icon] || Home;
            return (
              <div key={r.id} className="rounded-2xl border bg-white p-4 flex items-center gap-3" style={{ borderColor: palette.border }} data-testid={`household-rhythm-${r.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${palette.primary}15` }}>
                  <Icon className="w-5 h-5" style={{ color: palette.primary }} />
                </div>
                <div>
                  <p className="text-sm font-bold">{r.title}</p>
                  <p className="text-xs opacity-70">{r.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Shared check-in */}
      <div className="rounded-2xl border bg-white p-5 text-center" style={{ borderColor: palette.border }} data-testid="home-checkin-card">
        <HeartHandshake className="w-8 h-8 mx-auto mb-2" style={{ color: palette.primary }} />
        <h2 className="text-lg font-bold font-body mb-1" style={{ color: palette.primary }}>Home Rhythm Check-in</h2>
        <p className="text-xs opacity-70 mb-4">
          One tap when the house feels in rhythm today — dinner shared, screens down, moments caught.
        </p>
        {homeStatus?.checked_in_today ? (
          <div className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full" style={{ backgroundColor: `${palette.primary}15`, color: palette.primary }} data-testid="home-checked-in">
            <Check className="w-4 h-4" /> Checked in today
          </div>
        ) : (
          <button
            onClick={homeCheckin}
            className="text-sm font-bold px-6 py-3 rounded-full text-white"
            style={{ backgroundColor: palette.primary }}
            data-testid="home-checkin-btn"
          >
            We're in rhythm today
          </button>
        )}
        {homeStatus?.total_checkins > 0 && (
          <p className="text-[11px] opacity-60 mt-3">{homeStatus.total_checkins} rhythm day{homeStatus.total_checkins === 1 ? "" : "s"} logged so far</p>
        )}
      </div>
    </div>
  );
};
